import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, OnDestroy, inject, input } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { first, take } from 'rxjs';

import { UpdateShippingAddressUseCase } from '../../../application/use-cases/update-shipping-address/update-shipping-address.use-case';
import { UpdatableShippingOrder } from '../../../domain/models/update-order-shipping-link.models';
import { OrderRepositoyInterface } from '../../../domain/ports/secondary/order-repository.interface';
import { ApiOrderRepository } from '../../../infrastructure/api/api-order.repository';
import { SidebarAdapter } from '../../../infrastructure/features/sidebar/sidebar.adapter';
import { ORDER_REPOSITORY } from '../../tokens/update-order-shipping.tokens';

import { getStoreAndLanguage } from '@core/infrastructure/state/core.selectors';
import { HttpRequestsService } from '@core/services/http-requests/http-requests.service';
import { RegisterSidebarCustomComponent, UnregisterSidebarCustomComponent } from '@features/sidebar';
import { AddressModule } from '@shared/address/address.module';
import { LoadAddresses, SetSelectedAddressById } from '@shared/address/store/address/address.actions';
import { getAllAddresses } from '@shared/address/store/address/address.selectors';
import { getSelectedShippingAddress } from '@shared/shipping/store/shipping.selectors';
import { DialogMargins, DialogService, DialogSizes } from '@ui/dialog/dialog.service';

@Component({
    selector: 'update-order-delivery-address',
    templateUrl: './update-order-delivery-address.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    providers: [{
        provide: ORDER_REPOSITORY,
        useFactory: () => new ApiOrderRepository(inject(HttpClient), inject(HttpRequestsService)),
    }],
    imports: [
        AddressModule,
        TranslateModule,
    ],
})
export class UpdateOrderDeliveryAddressComponent implements OnDestroy {
    private readonly store: Store = inject(Store);

    private readonly orderRepository: OrderRepositoyInterface = inject(ORDER_REPOSITORY);

    private readonly dialogService: DialogService = inject(DialogService);

    private readonly router: Router = inject(Router);

    public order = input<UpdatableShippingOrder>();

    constructor() {
        this.getUserAddressbook();
        this.registerEditFormComponent();
    }

    public ngOnDestroy(): void {
        // eslint-disable-next-line no-secrets/no-secrets
        UnregisterSidebarCustomComponent('PaymentMethodsConfirmDialogComponent');
    }

    public editAddress(_isNewAddress: boolean): void {
        SidebarAdapter.openSidebar(this.store, this.closeSidebar.bind(this));
    }

    public updateShippingAddress(): void {
        const selectedAddress = this.store.selectSignal(getSelectedShippingAddress)();

        const { storeId, languageId, languageCode } = this.store.selectSignal(getStoreAndLanguage)();

        UpdateShippingAddressUseCase(this.orderRepository, +storeId, +languageId, this.order(), selectedAddress)
            .pipe(take(1))
            .subscribe((result) => {
                const title = result.success
                    ? 'dev.myOrders.changeAddress.feedbackOK'
                    : result.error?.description ?? 'dev.myOrders.changeAddress.error';

                this.dialogService.openModalFromComponent(
                    title,
                    undefined,
                    undefined,
                    DialogSizes.SIZE_S,
                    DialogMargins.MARGIN_16,
                ).afterClosed$.pipe(take(1)).subscribe(() => {
                    this.router.navigate([languageCode, 'my-account', 'orders-type', 'online', this.order().id]);
                });
            });
    }

    private getUserAddressbook() {
        this.store.dispatch(LoadAddresses());

        this.store.select(getAllAddresses).pipe(
            first((addresses) => !!addresses.length)
        ).subscribe(() => {
            this.store.dispatch(SetSelectedAddressById({ id: this.order().shipping.shippingData.addressId }));
        });
    }

    private closeSidebar(_success: boolean) {
        SidebarAdapter.closeSidebar(this.store);
    }

    private registerEditFormComponent() {
        RegisterSidebarCustomComponent(
            // eslint-disable-next-line no-secrets/no-secrets
            'UpdateOrderDeliveryAddressFormComponent',
            () => import('../update-order-delivery-address-form/update-order-delivery-address-form.component').then(
                (m) => m.UpdateOrderDeliveryAddressFormComponent
            ),
        );
    }
}
